import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FCM } from "@capacitor-community/fcm";
import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import {
  ActionPerformed,
  PushNotifications,
  PushNotificationSchema,
  Token
} from "@capacitor/push-notifications";
import {
  getToken as firebaseGetToken,
  Messaging,
  onMessage as firebaseOnMessage
} from "firebase/messaging";

import { getPath } from "helpers/convertFormData";
import { showSuccessMessage } from "helpers/toastMessage";
import { IActivityItem } from "models/Home";
import { uploadFirebaseToken } from "services/api/user";
import { RootState } from "../store";

const goDetails = async (data: IActivityItem) => {
  const navigate = useNavigate();
  const { account } = useSelector((state: RootState) => state.authState);

  const path = getPath(data, account);

  if (path !== "") {
    navigate(path);
  }
};

const usePushNotifications = (messaging: Messaging | undefined) => {
  const { userToken } = useSelector((state: RootState) => state.authState);

  useEffect(() => {
    const initializePush = async () => {
      const TOKEN_STORAGE_KEY = "firebaseToken";

      const uploadTokenIfNeeded = async (token: string) => {
        const storedToken = localStorage.getItem(TOKEN_STORAGE_KEY);
        if (storedToken !== token) {
          const success = await uploadFirebaseToken(token);
          if (success) {
            localStorage.setItem(TOKEN_STORAGE_KEY, token);
          } else {
            // console.error("Failed to upload token to backend.");
          }
        }
      };

      const isNative = Capacitor.isNativePlatform();

      if (isNative) {
        // Mobile (iOS & Android)
        let permStatus = await PushNotifications.checkPermissions();
        if (permStatus.receive !== "granted") {
          permStatus = await PushNotifications.requestPermissions();
        }
        if (permStatus.receive !== "granted") {
          // console.warn("Push notification permission not granted");
          return;
        }

        await PushNotifications.register();

        PushNotifications.addListener("registration", async (token: Token) => {
          await uploadTokenIfNeeded(token.value);
        });

        PushNotifications.addListener("registrationError", (_error: any) => {
          // console.error("Push registration error:", error);
        });

        PushNotifications.addListener(
          "pushNotificationReceived",
          async (_notification: PushNotificationSchema) => {
            const { isActive } = await App.getState();
            if (isActive) {
              showSuccessMessage(_notification.data.message);
            }
          }
        );

        PushNotifications.addListener(
          "pushNotificationActionPerformed",
          (_action: ActionPerformed) => {
            goDetails(_action.notification.data);
          }
        );

        try {
          const fcmTokenResponse = await FCM.getToken();
          await uploadTokenIfNeeded(fcmTokenResponse.token);
        } catch (error) {
          // console.error("Error getting FCM token:", error);
        }

        try {
          const refreshedTokenResponse = await FCM.refreshToken();
          await uploadTokenIfNeeded(refreshedTokenResponse.token);
        } catch {
          // console.error("Error refreshing FCM token:", error);
        }
      } else {
        // Web Platform
        const permission = await Notification.requestPermission();
        if (permission !== "granted") {
          // console.warn("Push notification permission not granted");
          return;
        }

        if (messaging) {
          try {
            const swRegistration = await navigator.serviceWorker.ready;

            const currentToken = await firebaseGetToken(messaging, {
              vapidKey: process.env.REACT_APP_VAPID,
              serviceWorkerRegistration: swRegistration
            });

            if (currentToken) {
              await uploadTokenIfNeeded(currentToken);
            } else {
              // console.warn("No registration token available.");
            }
          } catch {
            // console.error("Error getting FCM token:", error);
          }

          firebaseOnMessage(messaging, (_payload) => {
            // console.log("Message received:", payload);
          });
        }
      }
    };

    if (userToken) {
      initializePush();
    }
  }, [userToken, messaging]);
};

export default usePushNotifications;
